import {POPUP} from "/@back@/commons/widgets/popups.js";
import {IInfo, IInfoBroker, IInfoProducer} from "/@lib@/commons/infos.js";
import {EHttpStatusCode, IBody, IO, IResponse} from "/@lib@/commons/io/io.js";
import {IReg, REG} from "/@lib@/commons/registry.js";
import {CDM} from "/@lib@/commons/utils/cdm.js";
import {ESrcField, ESrcSt, JSrcFields, JSrcFieldsTree, JSrcIdent, JSubItem, SRC, srcRef, srcUri} from "/@lib@/wsp/src.js";
import {IWspUiEnv, WSP, Wsp, wspCd, wspUri} from "/@lib@/wsp/wsp.js";
import {EWspChangesEvts, WspsLivePlace} from "/@lib@/wsp/wspsLive.js";
import {EItemTypeFamily, IItemPubNode, ItemType, WspMetaUi} from "/@lib@/wsp/wspMetaUi.js";
import {JLastDatas} from "/@lib@/commons/lastDatas.js";
import {getFilesOnDrop, IFolder} from "/@lib@/commons/io/files.js";
import {IXAddr} from "/@lib@/commons/xml/xAddr.js";
import {JRemoteResDescFromClient} from "/@lib@/core/remote.js";
import {CLIPBOARD} from "/@lib@/commons/clipboard.js";


/** Refs qui peuvent gérer un pointeur vers un subItem. */
export type srcUriSub = string;
export type srcIdSub = string;
export type srcRefSub = srcUriSub | srcIdSub;


export const enum EItStatus {
	'null' = -1,
	'conflict' = -2,
	'unknown' = -99,
	'ok' = 1,
	'warnings' = 2,
	'errors' = 3
}

export const enum EItResp {
	'undefined' = -1,
	'ok' = 1,
	'errors' = 3
}

/**
 * Interface des actionContext pour les actions relatives à un item/space
 * ou un ensemble d'item/spaces.
 */
export interface IShortDescCtx {
	/** Jamais null, mais peut être [] si sel ou itemPtr vide. */
	shortDescs: JSrcFields[]
	reg: IReg<IWspUiEnv>
	infoBroker?: IInfoBroker
	/** Ui d'appel, idéalement focusable pour reselection après dialogue. */
	emitter: HTMLElement
}

//XXX passer en liste pour faciliter les surcharges.
REG.reg.setPref("groupOrder.wsp.shortDesc", 1, "open clipboard ptrEdit localEdit ins edit clone tools * importExport");

export interface IItem extends JSrcFields {
	wsp: Wsp;
	srcUri: srcUri;
}

interface JVersions {
	vers?: JSrcFields[];
}


export const enum EGenStatus {
	working = 'working',
	ok = 'ok',
	null = 'null',
	failed = 'failed',
	warning = 'warning',
}

export interface JGenInfo {
	codeGenStack: string
	status: EGenStatus
	reason?: 'destinationLocked'
	title?: string
	skin?: string
	storedProps?: Jsonisable
	lastGen?: number
	skins?: JGenSkin[]
	uriPub?: string
	uriTraces?: string
	mimeDownload?: string
	extrasInfos?: Jsonisable
	/** on desktop */
	localPathPub?: string
}

export interface JGenSkin {
	code: string
	title: string
	hasIllus?: boolean
}

/**
 * Message reçu par l'itemViewer indiquant qu'il doit afficher et mettre le focus sur ce srcRef.
 * Fournit le le srcRef mais aussi le shortDesc si dispo.
 * Le lastDatas sert notamment pour l'init à la création de tasks.
 */
export class InfoFocusItem implements IInfo {
	handled: boolean;

	constructor(public srcRef: srcRef, public shortDesc?: JSrcFields, public lastDatas?: JLastDatas) {
	}

	infoHolders?: IInfoProducer[];
}

/**
 * Message indiquant aux widgets affichant des listes de sources de sélectionner une ou plsuieurs sources.
 * Approche générique pour faire réagir  l'interface suite à une action d'édition sur la ou les sources pointées.
 * Contrairement à InfoFocusItem, permet de mettre en évidence le résultat de l'action sur N sources et sur des
 * sources qui ne sont pas des items visibles dans l'itemViewer (des espaces par exemple).
 * TODO revoir en générique les actions Spécifiques à SpaceTree en passant par ce InfoSelectUri ?
 */
export class InfoSelectUris implements IInfo {
	handled: boolean;

	constructor(public srcUris: srcUri[]) {
	}

	infoHolders?: IInfoProducer[];
}

/**
 * Message reçu par l'itemViewer indiquant qu'il doit afficher et mettre le focus sur ce srcRef
 * et pointer un xapth précis.
 */
export class InfoFocusNodeInItem extends InfoFocusItem {
	constructor(public xpath: string | IXAddr, srcRef: srcRef, shortDesc?: JSrcFields, lastDatas?: JLastDatas) {
		super(srcRef, shortDesc, lastDatas);
	}
}


/** Message reçu par l'itemViewer indiquant qu'il doit ouvrir un ensemble d'items, et optionnellement de mettre le focus sur un item. */
export class InfoOpenItems implements IInfo {
	/** item à ouvrir, sans les focuser. */
	srcRefs: srcRef[];
	/** item à focuser. */
	srcRef?: srcRef;

	constructor(srcRefs: srcRef[], srcRef: srcRef) {
		this.srcRefs = srcRefs;
		this.srcRef = srcRef;
	}

	infoHolders?: IInfoProducer[];
}

/** Message émis par l'itemViewer indiquant qu'un ordre de refresh de la visualisation d'un item a été émis */
export class InfoRefreshItemDisplays implements IInfo {

	constructor(public srcRef: srcRef) {}

	infoHolders?: IInfoProducer[];
}

/** Message diffusé par l'itemViewer pour signifier l'item actuellement courant. */
export class InfoCurrentItem implements IInfo {
	/** null pour signifier aucun item courant. */
	srcUri: srcUri;
	/** shortDesc de l'item courant si srcUri non null.*/
	shortDesc?: JSrcFields;

	constructor(srcUri: srcUri, shortDesc?: JSrcFields) {
		this.srcUri = srcUri;
		this.shortDesc = shortDesc;
	}

	infoHolders?: IInfoProducer[];
}

/** Message requête pour demander l'item actuellement courant. */
export class InfoReqCurrentItem implements IInfo {
	/** null pour signifier aucun item courant. */
	srcUri?: srcUri;
	/** shortDesc de l'item courant si srcUri non null.*/
	shortDesc?: JSrcFields;

	infoHolders?: IInfoProducer[];
}

/** Message indiquant qu'il faudrait mettre en évidence les items sélectionnables avec cette signature. */
export class InfoHighlighItemSgn implements IInfo {

	/** Pattern des gn des items à highlighter. null pour arrêter l'highligting. */
	sgnPattern: RegExp;

	/** Item actuellement assigné, donc à ne pas highlighter (ou pas de la même manière). */
	assigned: srcRef;

	constructor(sgnPattern: RegExp, assigned?: srcRef) {
		this.sgnPattern = sgnPattern;
		if (assigned) this.assigned = assigned;
	}

	infoHolders?: IInfoProducer[];
}

/**
 * Informe d'un changement d'uri d'une source que devrait suivre (itemViewer).
 * Cas d'usage :
 * - création d'une task (srcUri en "new:.." transfomée en srcUri réelle)
 * - renommage / déplacement d'une source (TODO rename/move event issu du serveur)
 */
export class InfoSrcUriMoved implements IInfo {
	constructor(readonly oldSrcUri: string, readonly newSrcUri: string) {}

	infoHolders?: IInfoProducer[];
}


/** Liste des refs d'items pour la sérialisation / désérialisation json. */
export interface JShortDescsTransfer {
	sdTrsfId: string
	/** URL du server chain. null si import externe à un server chain. */
	server?: string | null
	/** null si import externe à un server SC. */
	wspCd?: wspCd | null
	/** null en cas de création dynamique. cf IShortDescsTransfer.shortDescs. */
	srcRefs?: srcRefSub[] | null
}

export function isJShortDescsTransfer(s: any): s is JShortDescsTransfer {return s && s.sdTrsfId}

/** Représente un ensemble de sources issu du presse-papier ou d'une session de drag&drop. */
export interface IShortDescsTransfer extends JShortDescsTransfer {

	/** Atelier d'origine (donc source si issue du même serveur que la cible). */
	wsp?: Wsp

	/** Si les sources sont externes et doivent être importées (fichier du disque, url externe, src d'un autre wsp...). */
	isImport?: boolean

	/** Si isImport===true et que la source sont des urls externes. */
	isImportUrls?: boolean

	/**
	 * Pour imports dynamiques (isImport===true), permet d'évaluer si le contenu ne pourra pas matcher le pattern de la signature cible.
	 * Cette méthode peut être appelée avant et après extractDatasNow(), la méthode doit faire avec le maximum
	 * d'infos à sa disposition au stade où elle est appelée.
	 * Doit retourner false dans le doute ou si au moins une entrée de shortDescs matchera patternItemSgn.
	 * Doit retourner true si toutes les entrées de shortDescs ne matcheront pas patternItemSgn.
	 */
	rejectSgnMatch?(patternItemSgn: RegExp): boolean

	/**
	 * En cas de drag and drop : cette méthode doit être appelée au drop car certaines données (files...)
	 * ne sont accessibles auprès du DataTransfer qu'au cours du drop.
	 *
	 * En cas de paste du clipboard : doit être appelé pour charger les données asynchrones (remoteResDesc...)
	 *
	 * Retourne 'stop' si après récupération de ces datas précisant les types possibles, on constate
	 * que ce ISkImporter n'est plus possible.
	 */
	extractDatasNow?(data?: DataTransfer): Promise<void | 'stop'>

	/**
	 * Liste des shortDesc chargés dans le contexte courant.
	 * Attention, données non mises à jour dynamiquement, pourraient être obsolètes.
	 *
	 * Si isImport===true, les shortDescs sont partiels (limité à itModel?, itSgn?) enrichi d'une méthode asycnhrone createSrc().
	 * Le contenu n'est réellement créé dans l'atelier qu'à l'appel de IShortDescTransfer.createSrc() retournant
	 * les shortDescs fields définitifs. Cette méthode est aussi apellée dans le contexte d'un Transfer d'un autre atelier.
	 */
	shortDescs: IShortDescTransfer[]
}

export interface IShortDescTransfer extends JSrcFields {
	/**
	 * Identifiant unique de l'élément en cours de transfert, quelque soit sa morphologie
	 */
	fromUid?: string

	/**
	 * Si IShortDescsTransfer.isImport===true, les shortDescs sont partiels (limité à itModel?, itSgn?) enrichis
	 * d'une méthode asycnhrone createSrc().
	 *
	 * Le contenu n'est réellement créé dans l'atelier qu'à l'appel de createSrc() retournant
	 * les shortDescs fields définitifs. Cette méthode est aussi appelée dans le contexte d'un Transfer d'un autre atelier.
	 */
	createSrc?: (options: OImportSrcOptions) => Promise<JSrcFields | null>
}


export function isShortDescsTransfer(s: any): s is IShortDescsTransfer {return s && s.shortDescs && isJShortDescsTransfer(s)}

/**
 * IShortDescsTransfer issu de contexte externe : FileSystem, UriList, (à venir: au server chain ?)
 */
abstract class ShortDescsExternal implements IShortDescsTransfer {

	shortDescs: IShortDescTransfer[] = [];

	get sdTrsfId(): string {return 'external'}

	get server(): string {return null}

	get wspCd(): wspCd {return null}

	get srcRefs(): srcRef[] {return null}

	get isImport(): boolean {return true};

	constructor(public reg: IReg<IWspUiEnv>, public uiCtx: HTMLElement, public fromDrag: boolean) {
		//this.wsp = reg.env.wsp; NON this.wsp doit représenter la source, reg = l'env cible.
	}
}

/** Liste de shortDescs potentiels à importer à partir de fichiers externes. */
class ShortDescsFromFile extends ShortDescsExternal {
	_files: (File | IFolder)[];
	_importDone: Set<number>;

	// get potentialItSgns(): Set<string> {
	// 	this.reg.env.wsp.wspMetaUi.
	// }

	async extractDatasNow(data?: DataTransfer) {
		if (!this._files && data) {
			if (this.fromDrag) {
				this._files = await getFilesOnDrop(data);
			} else {
				this._files = Array.from(data.files);
			}
		}
	}

	rejectSgnMatch(patternItemSgn: RegExp): boolean {
		for (let i = 0; i < this.shortDescs.length; i++) {
			const sd = this.shortDescs[i] as ShortDescToImportFromFile;
			if (!sd.itemType || patternItemSgn.test(sd.itemType.getSgn())) return false;
		}
		return true; //aucun itemType ne match
	}
}


class ShortDescToImportFromFile implements IShortDescTransfer {

	get fromUid(): string {
		return this.indexItem.toString();
	}

	/**
	 * @aparm itemType si connu.
	 */
	constructor(public transferInfos: ShortDescsFromFile, public itemType: ItemType | null, public indexItem: number) {
		transferInfos.shortDescs.push(this);
	}

	get itSgn(): string | undefined {return this.itemType?.getSgn()};

	get itModel(): string | undefined {return this.itemType?.getModel()};

	async createSrc(options: OImportSrcOptions): Promise<JSrcFields> {
		const svc = this.transferInfos.reg.getSvc<IImportInWspSvc>("wspImportInWsp");
		if (!svc) return null;
		if (!this.transferInfos._importDone) this.transferInfos._importDone = new Set();
		else if (this.transferInfos._importDone.has(this.indexItem)) return null; //déjà importé avec un autre type.
		this.transferInfos._importDone.add(this.indexItem);
		const file = this.transferInfos._files ? this.transferInfos._files[this.indexItem] : null;
		//console.log("ShortDescToImport.create:::", this.indexItem, file);
		if (!file) return null;
		return svc.importSrc(file, options, this.transferInfos.reg, this.transferInfos.uiCtx);
	}

}

/** Ensemble de ShortDescs à importer à partir d'un DataTransfer de type "text/uri-list" */
export class ShortDescsFromUrlList extends ShortDescsExternal {

	protected _extractDone = false;

	protected _customData: DataTransfer

	get isImportUrls() {return true}

	async extractDatasNow(data?: DataTransfer): Promise<void | 'stop'> {
		if (!this._extractDone && (this._customData || data)) {
			this._extractDone = true;
			const svc = this.reg.getSvc<IImportFromUrlSvc>("wspImportFromUrl");
			if (svc) this.shortDescs = await svc.extractShortDescs(this._customData || data, this.reg, this.uiCtx);
		}
		if (this.shortDescs.length === 0) return 'stop';
	}

	/** Pour paste clipboard ou imposer un dataTransfer custom. */
	setCustomDataTransfer(data: DataTransfer): this {
		this._customData = data;
		return this;
	}

	rejectSgnMatch(patternItemSgn: RegExp): boolean {
		const svc = this.reg.getSvc<IImportFromUrlSvc>("wspImportFromUrl");
		if (svc == null) return true;
		if (this._extractDone) {
			//Todo avec les infos dispo suite à extractDatasNow()
			// for (let i = 0; i < this.shortDescs.length; i++) {
			// 	const sd = this.shortDescs[i] as ShortDescToImportFromUrl;
			// 	if (!svc.rejectSgnMatch(sd.url, patternItemSgn, this.reg)) return false;
			// }
			return false;
		} else {
			return svc.rejectSgnMatch(patternItemSgn, this.reg);
		}
	}
}

/**
 * Svc d'import d'une source (fihier, blob ou folder) dans un atelier.
 * Code du svc: "wspImportInWsp".
 */
export interface IImportInWspSvc {

	/**
	 * Importe une source
	 * @return ShortDesc de l'item créé/remplacé ou null si abandon.
	 */
	importSrc(body: File | Blob | IFolder, options: OImportSrcOptions, reg: IReg<IWspUiEnv>, uiCtx: HTMLElement): Promise<JSrcFields | null>;
}

/**
 * Contexte initial d'un processus de manipulation d'un DataTransfer (phase de dragover en particulier).
 */
export interface OTransferSrcContext {
	/** Cible de l'import */
	targetSrcType: 'item' | 'space' | 'itemOrSpace' | 'res'

	/** Uri parent de l'import. */
	defaultUriParent?: srcUri

	/** Si cible restreinte à des items */
	sgnPattern?: RegExp

	/** Si cible restreinte à des items ou sub-items */
	subItSgnPattern?: RegExp

	/**
	 * Si true, accepte de créer des contenus à la volée dans l'atelier cible.
	 * cf OImportSrcOptions, IShortDescsTransfer.isImport et IShortDescTransfer.createSrc()
	 */
	canCreate?: boolean
}

/**
 * Options pour finaliser un processus d'import.
 * Cf IShortDescTransfer.createSrc(options: OImportSrcOptions)
 */
export interface OImportSrcOptions extends OTransferSrcContext {

	/** defaultUriParent est non modifiable. */
	uriParentFrozen?: boolean

	/** Autorise la possibilité de remplacer un contenu existant. */
	allowReplace?: boolean

	/**
	 * Si on est dans une session d'imports multiple, initialiser repeatedImports avec un {}
	 * pour offrir une mémoire des choix de l'utilisateur entre chaque import élémentaire de la session.
	 * Les écrans pourront afficher alors ce type d'options.
	 */
	repeatedImports?: OImportRepeatStates
}

export interface OImportRepeatStates extends Dict<any> {
	replaceAll?: boolean
	replaceNothing?: boolean
}


/**
 * Svc de référencement direct, de réplication (proxy) ou de duplication simple d'items inter-ateliers (même serveur).
 * Code du svc: "wspImportInterWsp".
 */
export interface IImportInterWspSvc {

	/**
	 * Importe une src issue d'un autre wsp du même serveur.
	 * Ce service peut laisser le choix à l'utilisateur et/ou imposer différentes approches :
	 * ref directe item étranger, réplication par proxy ou duplication simple.
	 *
	 * @return ShortDesc de l'item créé/remplacé ou null si abandon.
	 */
	importSrc(wspFrom: Wsp, srcFrom: JSrcFields, options: OImportInterWspOptions, reg: IReg<IWspUiEnv>, uiCtx: HTMLElement): Promise<JSrcFields | null>;
}

export interface OImportInterWspOptions extends OImportSrcOptions {

	/**
	 * Le contexte d'appel permet-il de retourner un lien direct vers un item étranger :
	 * cas d'un ptrItem dans un éditeur. Situation inacceptable : action de Coller dans le spaceTree.
	 */
	refExtItemAcceptable?: boolean
}


/**
 * Svc d'import à partir d'une url externe.
 * Code du svc: "wspImportFromUrl".
 */
export interface IImportFromUrlSvc {

	/**
	 * Evalue si ce pattern de signature d'item est incompatible avec cette source.
	 * Dans le doute retourner false.
	 * Appelé avant extractShortDescs(), dépend donc uniquement des capacités d'import de ce service
	 * sans préjuger de la nature des sources à extraire des urls cibles.
	 */
	rejectSgnMatch(patternItemSgn: RegExp, reg: IReg<IWspUiEnv>): boolean

	/**
	 * Parse le contenu de type "text/uri-list" et instancie les IShortDescTransfer.
	 * Async ici pour permettre au svc d'interroger les url parsées et d'enrichir les objets
	 * IShortDescTransfer retournées avec des infos du serveur distant.
	 */
	extractShortDescs(data: DataTransfer, reg: IReg<IWspUiEnv>, uiCtx: HTMLElement): Promise<IShortDescUrlTransfer[]>

	/**
	 * Importe un item issu d'un autre wsp.
	 * Ce service peut laisser le choix à l'utilisateur et/ou imposer différentes approches :
	 * ref directe item étranger, réplication par proxy ou duplication simple.
	 *
	 * @return ShortDesc de l'item créé/remplacé ou null si abandon.
	 */
	importSrc(url: string, options: OImportSrcOptions, reg: IReg<IWspUiEnv>, uiCtx: HTMLElement): Promise<JSrcFields | null>
}

/**
 * Sur-typage de IShortDescTransfer si IShortDescsTransfer.isImportUrls===true.
 */
export interface IShortDescUrlTransfer extends IShortDescTransfer {
	url: string

	/**
	 * Utilisable après que IShortDescsTransfer.extractDatasNow() ait été appelé
	 * (donc pas accessible pendant un drag).
	 */
	getRemoteResDesc(): Promise<JRemoteResDescFromClient | null>

	/**
	 * Si IShortDescsTransfer.isImportUrls===true, et RemoteResDesc déjà évalué.
	 * @return undefined si pas encore évalué, null si aucun remoteResDesc dispo pour cette url.
	 */
	remoteResDesc?: JRemoteResDescFromClient | null
}

/**
 * Utilitaire mémorisant une liste de nœuds fils permettant de controler l'existance d'un code d'item
 * dans un espace (ou un item multi-res) donné.
 * L'usage de cet objet doit être éphémère, l'implémentation actuelle n'écoute pas les wspChanges pour mettre à jour la liste des fils.
 */
export class FolderEntries {

	children = new Set<string>();

	loading: Promise<this>;

	folderUri: string;

	constructor(public wsp: Wsp, public uiContext: HTMLElement) {}


	setFolderUri(folderUri: string): Promise<this> {
		this.folderUri = folderUri;
		this.loading = WSP.fetchSrcTree(this.wsp, this.uiContext, folderUri, 1, ["srcNm"])
			.then((tree: JSrcFieldsTree) => {
				this.children = tree.ch ? new Set(tree.ch.map((e) => e.srcNm)) : new Set();
				this.loading = null;
				return this;
			}, () => {
				this.children = null;
				this.loading = null;
				return this;
			});
		return this.loading;
	}

	async getChildState(childName: string): Promise<EFolderChildState> {
		return this.loading ? this.loading.then(() => this.getChildStateNow(childName)) : this.getChildStateNow(childName);
	}

	getChildStateNow(childName: string): EFolderChildState {
		if (this.children?.has(childName)) return EFolderChildState.used;
		return EFolderChildState.free;
	}
}

export const enum EFolderChildState {
	used = 'used',
	free = 'free'
}

/** Svc pour obtenir icones et libellés d'une source. */
export interface ISrcRenderer {

	/** Retourne l'icone d'une source. */
	getIcon(src: JSrcFields, wspMetaUi: WspMetaUi, open?: boolean): string

	/**
	 * Retourne le 1er nom d'une source (item, espace, task...)
	 * Usages standards :
	 * - pour un item/espace : le code de l'item ou de l'espace
	 * - pour une task : le Title de la task
	 */
	getMainName(src: JSrcFields, wspMetaUi: WspMetaUi, notAlone?: boolean): string

	/**
	 * Retourne le 2ème nom d'une source (item, espace, task...)
	 * Usages standards :
	 * - pour un item : le titre de l'item
	 * - pour un espace : null
	 * - pour une task : null
	 */
	getSecondName(src: JSrcFields, wspMetaUi: WspMetaUi): string
}

export class SrcRenderer implements ISrcRenderer {
	getIcon(src: JSrcFields, wspMetaUi: WspMetaUi, open?: boolean): string {
		if (src.itModel != null) {
			//item ou res d'item
			const itemType = wspMetaUi.getItemType(src.itModel);
			return open ? itemType.getIconOpen(src) : itemType.getIcon(src);
		}
		//space
		return open ? wspMetaUi.getSpaceOpenIcon(src) : wspMetaUi.getSpaceIcon(src);
	}

	getMainName(src: JSrcFields, wspMetaUi: WspMetaUi, notAlone?: boolean): string {
		if (src.itModel != null) {
			const itemType = wspMetaUi.getItemType(src.itModel);
			if (itemType.getFamily() === EItemTypeFamily.task) return src.actTi || itemType.getTitle(src);
			if (!notAlone && src.itSubItem) return SRC.extractLeafFromUri(src.srcUri) + " ⤷ " + ITEM.getSubRefTitle(src);
			else return SRC.extractLeafFromUri(src.srcUri);
		}
		//space
		if (src.srcUri) {
			if (src.srcUri === "/~air") return "[Items flottants]";
			if (src.srcUri === "/~ext") return "[Items étrangers]";
			return SRC.extractLeafFromUri(src.srcUri);
		}
		return src.srcUri == null ? "[Non trouvé]" : "[Racine de l'atelier]";
	}

	getSecondName(src: JSrcFields, wspMetaUi: WspMetaUi): string {
		if (src.itModel != null) {
			const itemType = wspMetaUi.getItemType(src.itModel);
			if (itemType.getFamily() === EItemTypeFamily.task) return src.actTi ? itemType.getTitle(src) : null;
		}
		return ITEM.getTitle(src);
	}
}


export interface JItemNetResp {
	/** Origine du lien */
	src: JSrcFields

	/** Cible du lien */
	lnks: JItemNetLnkResp[]
}

export interface JItemNetLnkResp {

	/** SubItemId au départ du lien */
	subIt: string

	/** Destination du lien */
	src: JSrcFields

	/**
	 * Si itemTargeted non renseigné : lien(s) vers cet item#subItem.
	 * Si mergeLinks=true, dictionnaire des codes des lien avec le nombre d'instances
	 * Sinon code du lien.
	 */
	nm?: Dict<number> | string

	/**
	 * Si itemTargeted renseigné, liste des chemins : couple(lien + item traversé) + lastLink.
	 * Le lien est une string ou un dictionnaire de liens en fonction du paramètre mergeLinks de la requête.
	 */
	via?: [Dict<number> | string, JSrcFields /* last: string | Dict<number>*/][]
}


export namespace ITEM {

	export const KEY_SrcRenderer = "SrcRenderer";

	/** Spécification libre d'une transformUri définissant le skn à utiliser
	 * @see  eu.scenari.wsp.service.gen.SvcWspGenDialog.SKIN_BY_URI_PREFIX */
	export const GEN_SKIN_BY_URI_PREFIX = "skinUri:";

	export function srcRefSub(item: JSrcFields): srcRefSub {return item.itSubItem ? `${item.srcId || item.srcUri}#${item.itSubItem.id}` : item.srcId || item.srcUri}

	export function srcUriSub(item: JSrcFields): srcUriSub {return item.itSubItem ? `${item.srcUri}#${item.itSubItem.id}` : item.srcUri}

	export function builSrcRefSub(srcRef: srcRef, id: string): srcUriSub {return `${srcRef}#${id}`}

	export function getTitle(src: JSrcFields): string {
		if (src.itSubItem) {
			const subTi = src.itSubItem.ti || src.itSubItem.id;
			return subTi ? `${src.itTi || ""} ⤷${subTi}` : src.itTi || "";
		}
		return src.itTi || src.actTi || "";
	}

	export function getSubRefTitle(src: JSrcFields): string {
		return src.itSubItem?.ti || "";
	}

	export function getSrcMainName(reg: IReg<any>, src: JSrcFields, wspMetaUi: WspMetaUi, notAlone?: boolean): string {
		return reg.getSvc<ISrcRenderer>(ITEM.KEY_SrcRenderer).getMainName(src, wspMetaUi, notAlone);
	}

	export function getSrcSecondName(reg: IReg<any>, src: JSrcFields, wspMetaUi: WspMetaUi): string {
		return reg.getSvc<ISrcRenderer>(ITEM.KEY_SrcRenderer).getSecondName(src, wspMetaUi);
	}

	export function getSrcIcon(reg: IReg<any>, src: JSrcFields, wspMetaUi: WspMetaUi, open?: boolean): string {
		return reg.getSvc<ISrcRenderer>(ITEM.KEY_SrcRenderer).getIcon(src, wspMetaUi, open);
	}


	export function getSrcUriType(srcUri: srcUri | wspUri): 'item' | 'space' | 'res' {
		const point = srcUri.indexOf('.');
		if (point < 0) return 'space';
		const subSlash = srcUri.indexOf('/', point);
		return subSlash >= 0 && srcUri.length > subSlash + 1 ? 'res' : 'item';
	}

	/** Enumération des types d'uriRes d'un item. */
	export const enum EUriResType {
		root,
		folder,
		file
	}

	export function getUriResType(shortDesc: JSrcFields): EUriResType | null {
		const srcUri = shortDesc.srcUri;
		const offsetPoint = srcUri.indexOf('.');
		if (offsetPoint < 0) return null;
		const offsetSubRes = srcUri.indexOf('/', offsetPoint + 1);
		if (offsetSubRes < 0) return EUriResType.root;
		switch (shortDesc.srcSt) {
		case ESrcSt.file :
			return EUriResType.file;
		case ESrcSt.folder :
			return EUriResType.folder;
		default: //la ressource n'existe pas
			return (srcUri.lastIndexOf('/') < srcUri.lastIndexOf('.')) ? EUriResType.file : EUriResType.folder;
		}
	}

	/** Note : un label d'espace n'est pas une srcUri (ne commence pas par un '/'). */
	export function extractSpaceLabel(srcUri: srcUri, ifRootOrSpecial: string | null = "[Racine de l'atelier]"): string {
		if (!srcUri || isAirItem(srcUri) || isExtItem(srcUri)) return ifRootOrSpecial;
		const idx = srcUri.indexOf(".");
		if (idx < 0) return srcUri.substring(1);
		const idx2 = srcUri.lastIndexOf("/", idx);
		return idx2 > 0 ? srcUri.substring(1, idx2) : ifRootOrSpecial;
	}

	/** Note : un label d'espace ou de folderRes n'est pas une srcUri (ne commence pas par un '/'). */
	export function extractSrcLabel(srcUri: srcUri, ifRoot: string): string {
		if (!srcUri) return ifRoot;
		if (isAirItem(srcUri) || isExtItem(srcUri)) {
			//On fait sauter ce qui précède le code de l'item
			return srcUri.substring(srcUri.lastIndexOf("/", srcUri.indexOf('.')) + 1);
		}
		return srcUri.substring(1) || ifRoot;
	}

	export function getItemUriLabel(srcUri: srcUri, shortDesc?: JSrcFields): string {
		if (!srcUri) return "/";
		if (isSpecialUri(srcUri)) {
			if (isAirItem(srcUri)) return extractItemCode(srcUri) + " (item flottant)";
			if (isExtItem(srcUri)) return extractItemCode(srcUri) + " (item étranger)";
			if (isTrashUri(srcUri)) return extractItemCode(srcUri) + " (en corbeille)";
			if (isHistoryUri(srcUri)) return extractItemCode(srcUri) + " (historique)";
		}
		if (shortDesc && shortDesc.itSubItem)
			return srcUri + " ⤷ " + shortDesc.itSubItem.id
		else
			return srcUri;
	}

	export function extractSpaceUri(srcUri: srcUri): srcUri {
		if (!srcUri || isAirItem(srcUri) || isExtItem(srcUri) || isAnnot(srcUri)) return "";
		const idx = srcUri.indexOf(".");
		if (idx < 0) return srcUri;
		const idx2 = srcUri.lastIndexOf("/", idx);
		return idx2 > 0 ? srcUri.substring(0, idx2) : "";
	}

	export function extractItemUri<T extends srcUri | wspUri>(srcUri: T): T | null {
		const idx = srcUri.indexOf(".");
		if (idx < 0) return null;
		const idx2 = srcUri.indexOf("/", idx + 1);
		return idx2 > 0 ? srcUri.substring(0, idx2) as T : srcUri;
	}

	export function extractItemCode(srcUri: srcUri): string | null {
		const idx = srcUri.indexOf(".");
		if (idx < 0) return null;
		const idx0 = srcUri.lastIndexOf("/", idx) + 1;
		const idx2 = srcUri.indexOf("/", idx + 1);
		return idx2 > 0 ? srcUri.substring(idx0, idx2) : srcUri.substring(idx0);
	}

	export function extractItemResPath(srcUri: srcUri | wspUri): string | null {
		const idx = srcUri.indexOf(".");
		if (idx < 0) return null;
		const idx2 = srcUri.indexOf("/", idx + 1);
		return idx2 > 0 ? srcUri.substring(idx2 + 1) : null;
	}

	export function isValidCodeItem(name: string, fromForNotif?: Node): true | string {
		let r = SRC.isValidPartUri(name, fromForNotif);
		if (r === true) {
			if (name.indexOf('~') >= 0) r = "Le caractère '~' n'est pas autorisé dans un code d'item.";
			if (name.indexOf('.') < 0) r = "Un code d'item doit contenir une extension séparée par un '.'.";
			if (fromForNotif && r !== true) POPUP.showNotifWarning(r, fromForNotif);
		}
		return r;
	}

	export function isValidCode(type: 'item' | 'space' | 'res', name: string, fromForNotif?: Node): true | string {
		switch (type) {
		case "item":
			return ITEM.isValidCodeItem(name, fromForNotif);
		case "space":
			return ITEM.isValidCodeSpace(name, fromForNotif);
		}
		return SRC.isValidPartUri(name, fromForNotif);
	}

	export function isValidCodeSpace(name: string, fromForNotif?: Node): true | string {
		let r = SRC.isValidPartUri(name, fromForNotif);
		if (r === true) {
			if (name.indexOf('~') >= 0) r = "Le caractère '~' n'est pas autorisé dans un code d'espace.";
			if (name.indexOf('.') >= 0) r = "Un code d'espace ne doit pas contenir de '.'.";
			if (fromForNotif && r !== true) POPUP.showNotifWarning(r, fromForNotif);
		}
		return r;
	}

	export function isSrcRefSub(srcRef: srcRefSub): boolean {
		return srcRef && srcRef.indexOf('#') >= 0;
	}

	export function makeSrcRefSub(srcRef: srcRef, subItemId?: string): srcRefSub {
		if (!srcRef) return;
		return srcRef + (subItemId ? '#' + subItemId : '');
	}

	export function extractSubItemId(srcRef: srcRefSub): string {
		const i = srcRef?.indexOf('#');
		return i >= 0 ? srcRef.substring(i + 1) : null;
	}

	export function extractSrcRef(srcRefSub: srcRefSub, knownSubItemId?: string): srcRef {
		const i = knownSubItemId ? srcRefSub.length - knownSubItemId.length - 1 : srcRefSub?.indexOf('#');
		return i >= 0 ? srcRefSub.substring(0, i) : srcRefSub;
	}

	export function isSameSrcRef(srcRef: srcRef, srcRefSub: srcRef | srcRefSub) {
		if (!srcRef || !srcRefSub) return false;
		if (srcRefSub.length > srcRef.length) {
			return srcRefSub.charCodeAt(srcRef.length) === 35 /*'#'*/ && srcRefSub.startsWith(srcRef);
		}
		return srcRef === srcRefSub;
	}

	/** Cherche dans l'arbre des JSubItem le subItem avec cet id ou undefined si pas trouvé. */
	export function findSubItem(sid: string, subItems: JSubItem[]): JSubItem | undefined {
		if (!sid || !subItems) return undefined;

		function find(list: JSubItem[] | null): JSubItem {
			if (list) for (const subItem of list) {
				if (subItem.id === sid) return subItem;
				const ss = find(subItem.subItems);
				if (ss) return ss;
			}
			return undefined;
		}

		return find(subItems);
	}

	/** Retourne la hiérérchie de l'arbre des JSubItems jusqu'au subItem avec cet id ou undefined si pas trouvé. */
	export function findSubItemStack(sid: string, subItems: JSubItem[]): JSubItem[] | undefined {
		if (!sid || !subItems) return undefined;
		const stack: JSubItem[] = [];

		function find(list: JSubItem[] | null): boolean {
			if (list) for (const subItem of list) {
				if (subItem.id === sid) {
					stack.push(subItem);
					return true;
				}
				if (subItem.subItems) {
					stack.push(subItem);
					if (find(subItem.subItems)) return true;
					stack.pop();
				}
			}
			return false;
		}

		return find(subItems) ? stack : undefined;
	}

	/** Privilégie l'uri sur l'id dans le cas d'uri spéciales en corbeille, en histo (...) pour préserver ce contexte particulier. */
	export function srcRefInContext(ident: JSrcIdent): srcRef {
		if (isHistoryOrTrashUri(ident.srcUri)) return ident.srcUri;
		return ident.srcId || ident.srcUri
	}

	/** URI définissant une zone contrôlée (airItem, ...), ou de type "instruction" ({@link SRC.NEW_PREFIX}, ...) */
	export function isSpecialUri(srcUri: srcUri) {return srcUri && (srcUri.startsWith('/~') || srcUri.indexOf(":")) != -1}

	export const AIR_PREFIX = "/~air/";

	export function isAirItem(srcUri: srcUri) {return srcUri && srcUri.startsWith(AIR_PREFIX)}

	export const EXT_PREFIX = "/~ext/";

	export function isExtItem(srcUri: srcUri) {return srcUri && srcUri.startsWith(EXT_PREFIX)}

	export const TRANSIENT_PREFIX = "/~transient/";

	export function buildTansientUri(fromWspCode: string, srcUri: srcUri) {return TRANSIENT_PREFIX + fromWspCode + srcUri}

	export const ANNOT_PREFIX = "/~annot/";

	export function isAnnot(srcUri: srcUri) {return srcUri && srcUri.startsWith(ANNOT_PREFIX)}

	export const HISTOY_PREFIX = "/~history/";

	export function isHistoryUri(srcUri: srcUri) {return srcUri && srcUri.startsWith(HISTOY_PREFIX)}

	export const TRASH_PREFIX = "/~history~trash/";

	export function isTrashUri(srcUri: srcUri) {return srcUri && srcUri.startsWith(TRASH_PREFIX)}

	export function isHistoryOrTrashUri(srcUri: srcUri) {return srcUri && srcUri.startsWith("/~history")}

	export function isSrcUriInConflict(srcUri: srcUri) {
		return srcUri.indexOf("~conflict.") >= 0;
	}

	export async function fetchDom(wsp: Wsp, uiContext: HTMLElement, srcRef: srcRef, noneIfTrashed?: boolean): Promise<Document> {
		if (!wsp.isAvailable) await wsp.waitForAvailable(uiContext);
		const params = ["cdaction", "GetSrc", "format", "stream", "param", wsp.code, "refUri", srcRef, "204", ""];
		if (noneIfTrashed) params.push("srcTrashed", "false");
		return wsp.wspServer.config.wspSrcUrl.fetchDom(IO.qs(...params));
	}

	export async function fetchJson<R>(wsp: Wsp, uiContext: HTMLElement, srcRef: srcRef, noneIfTrashed?: boolean): Promise<R> {
		if (!wsp.isAvailable) await wsp.waitForAvailable(uiContext);
		const params = ["cdaction", "GetSrc", "format", "stream", "param", wsp.code, "refUri", srcRef, "204", ""];
		if (noneIfTrashed) params.push("srcTrashed", "false");
		return wsp.wspServer.config.wspSrcUrl.fetchJson(IO.qs(...params));
	}

	export async function fetchContent(wsp: Wsp, uiContext: HTMLElement, srcRef: srcRef, noneIfTrashed?: boolean, fields?: string | ESrcField[]): Promise<IResponse> {
		if (!wsp.isAvailable) await wsp.waitForAvailable(uiContext);
		const params = ["param", wsp.code, "refUri", srcRef, "format", "stream", "204", "", "fields", Array.isArray(fields) ? fields.join("*") : fields];
		if (noneIfTrashed) params.push("srcTrashed", "false");
		return wsp.wspServer.config.wspSrcUrl.fetch(IO.qs(...params));
	}

	export async function fetchSrcNetJson(wsp: Wsp, uiContext: HTMLElement, refUri: srcRef, fields: string | ESrcField[], dir?: "desc" | "asc", mergeLinks?: boolean, linksAllowed?: string, itemTargeted?: string, maxDepth?: number, maxTracks?: number, fieldsFrom?: string | ESrcField[], granularityFrom?: 'itemOnly'): Promise<JItemNetResp> {
		if (!wsp.isAvailable) await wsp.waitForAvailable(uiContext);
		if (dir === "asc" && wsp.infoWsp.allItemsLoaded !== "ok") await wsp.forceAllItemsLoaded(uiContext);
		return wsp.wspServer.config.wspSrcUrl.fetchJson(IO.qs("param", wsp.code, "refUri", refUri, "fields", Array.isArray(fields) ? fields.join("*") : fields, "format", "itemNetJson", "dir", dir, "mergeLinks", mergeLinks, "granularity", "subItem",
			"linksAllowed", linksAllowed, "itemTargeted", itemTargeted, "maxDepth", maxDepth, "maxTracks", maxTracks, "fieldsFrom", Array.isArray(fieldsFrom) ? fieldsFrom.join("*") : fieldsFrom || undefined, "granularityFrom", granularityFrom));
	}

	/**
	 * @param body Si null crée un dossier vide.
	 */
	export async function createItem(wsp: Wsp, fromPlace: WspsLivePlace | null, uiContext: HTMLElement, body: IBody | null, srcUri: srcUri): Promise<JSrcFields> {
		if (!wsp.isAvailable) await wsp.waitForAvailable(uiContext);
		const created = await wsp.wspServer.config.wspSrcUrl.fetchJson<JSrcIdent>(
			IO.qs("cdaction", "PutSrc", "param", wsp.code, "refUri", srcUri, "clId", wsp.wspServer.chain.clId, "asFolder", body != null ? null : "true", "fields", wsp.getShortDescDef()),
			{method: "PUT", body: body}
		);
		wsp.wspServer.wspsLive.dispatchLocalChange(fromPlace, wsp, created, EWspChangesEvts.u, created);
		return created;
	}

	/**
	 * @param body Si null crée un dossier vide.
	 * @param inFields A minima srcNm obligatoire
	 */
	export async function createAirItem(wsp: Wsp, fromPlace: WspsLivePlace | null, uiContext: HTMLElement, body: IBody, inFields: JSrcFields): Promise<JSrcFields> {
		if (!wsp.isAvailable) await wsp.waitForAvailable(uiContext);
		const created = await wsp.wspServer.config.wspSrcUrl.fetchJson(
			IO.qs("cdaction", "CreateAirItem", "param", wsp.code, "clId", wsp.wspServer.chain.clId, "options", CDM.stringify(inFields), "asFolder", body ? null : "true", "fields", wsp.getShortDescDef()),
			body ? {method: "PUT", body: body} : {method: "POST"}
		);
		wsp.wspServer.wspsLive.dispatchLocalChange(fromPlace, wsp, created, EWspChangesEvts.u, created);
		return created;
	}

	export async function createVersion(wsp: Wsp, uiContext: HTMLElement, refUri: srcRef, comment?: string): Promise<IItem> {
		if (!wsp.isAvailable) await wsp.waitForAvailable(uiContext);
		// TODO : changement côté Java pour récupérer directement la version (en une requête) ?
		await wsp.wspServer.config.versionUrl.fetchVoid(IO.qs("cdaction", "Create", "param", wsp.code, "refUri", refUri, "comment", comment), {method: "PUT"});
		const respJson: JSrcFields & JVersions = await wsp.wspServer.config.versionUrl.fetchJson(IO.qs("param", wsp.code, "refUri", refUri, "fields", "srcId*srcUri"));
		const id = respJson.vers[0].srcId;
		const srcUriVers = respJson.vers[0].srcUri;
		return {wsp: new Wsp(wsp.wspServer, SRC.extractIdFromSrcId(id)), srcUri: srcUriVers, srcId: id};
	}

	export async function update(wsp: Wsp, uiContext: HTMLElement, refUri: srcRef, content: IBody, progress?: (done: number, total: number | undefined) => void, fields?: string | ESrcField[]): Promise<JSrcFields> {
		if (!wsp.isAvailable) await wsp.waitForAvailable(uiContext);
		const qs = IO.qs("cdaction", "PutSrc", "param", wsp.code, "refUri", refUri, "asFolder", content == null ? "true" : undefined, "fields", Array.isArray(fields) ? fields.join('*') : fields || "srcUri");
		if (progress) {
			//En attendant une API plus propre https://github.com/whatwg/fetch/issues/607
			return new Promise<JSrcFields>((resolved, rejected) => {
				const xhr = new XMLHttpRequest();
				xhr.upload.addEventListener("progress", (ev) => {progress(ev.loaded, ev.lengthComputable ? ev.total : undefined)});
				xhr.open('PUT', wsp.wspServer.config.wspSrcUrl.url + qs);
				xhr.onload = (ev) => {
					let r: Jsonisable;
					try {
						r = JSON.parse(xhr.responseText);
					} catch (e) {
						rejected(e)
					}
					resolved(r)
				};
				xhr.onerror = rejected;
				xhr.send(content);
			});
		}
		return wsp.wspServer.config.wspSrcUrl.fetchJson<JSrcFields>(qs, {method: "PUT", body: content});
	}

	export async function importScar(wsp: Wsp, uiContext: HTMLElement, refUri: srcRef, content: File, replaceIfExist: boolean, progress?: (done: number, total: number | undefined) => void): Promise<{
		readonly status: EHttpStatusCode,
		asText?: string,
		error?: Error
	}> {
		if (!wsp.isAvailable) await wsp.waitForAvailable(uiContext);
		const qs = IO.qs("cdaction", "Import", "replaceIfExist", replaceIfExist, "wspTarget", wsp.code, "refUriTarget", refUri);
		if (progress) {
			//En attendant une API plus propre https://github.com/whatwg/fetch/issues/607
			return new Promise((resolved, rejected) => {
				const xhr = new XMLHttpRequest();
				xhr.upload.addEventListener("progress", (ev) => {progress(ev.loaded, ev.lengthComputable ? ev.total : undefined)});
				xhr.open('PUT', wsp.wspServer.config.importUrl.url + qs);
				xhr.onload = (ev) => resolved({status: xhr.status, asText: xhr.responseText});
				xhr.onerror = (ev) => resolved({status: xhr.status, error: new Error(xhr.statusText)});
				xhr.send(content);
			});
		}
		return wsp.wspServer.config.importUrl.fetch(qs, "text", {
			method: "PUT",
			body: content as any
		});
	}

	/** Responsabilités : modifie les responsabilités d'un utilisateur sur un ou plusiers items */
	export async function fetchUpdateRespByUser(wsp: Wsp, uiContext: HTMLElement, refUris: srcRef[], account: string, addResps?: string[], remResps?: []): Promise<void> {
		if (!wsp.isAvailable) await wsp.waitForAvailable(uiContext);
		const path = encodeURIComponent(wsp.code) + "/responsibilities" + IO.qs("cdaction", "UpdateResp", "user", account, "addResp", addResps.join(' '), "remResp", remResps.join(' '));
		return wsp.wspServer.config.wspSvcUrl.fetchVoid(path, {method: "PUT", body: IO.fd("refUris", refUris.join('\t'))});
	}

	/** Responsabilités : ajoute/supprime des utilisateurs à une resp donnée, sur un ou plusiers items */
	export async function fetchUpdateRespByRespMulti(wsp: Wsp, uiContext: HTMLElement, refUris: srcRef[], resp: string, addUsers?: string[], remUsers?: string[]): Promise<void> {
		const path = encodeURIComponent(wsp.code) + "/responsibilities" + IO.qs("cdaction", "UpdateResp", "resp", resp, "addUser", addUsers?.join(' '), "remUser", remUsers?.join(' '));
		return wsp.wspServer.config.wspSvcUrl.fetchVoid(path, {method: "PUT", body: IO.fd("refUris", refUris.join('\t'))});
	}

	/** Responsabilités : affecte un utilisateur unique à une resp donnée, sur un ou plusiers items */
	export async function fetchUpdateRespByRespSingle(wsp: Wsp, uiContext: HTMLElement, refUris: srcRef[], resp: string, singleUser: string): Promise<void> {
		const path = encodeURIComponent(wsp.code) + "/responsibilities" + IO.qs("cdaction", "UpdateResp", "resp", resp, "singleUser", singleUser);
		return wsp.wspServer.config.wspSvcUrl.fetchVoid(path, {method: "PUT", body: IO.fd("refUris", refUris.join('\t'))});
	}

	export async function execItemTransition(wsp: Wsp, uiContext: HTMLElement, transition: string, itemIdents: JSrcIdent[], paramsTransition?: { [refUri: string]: { [key: string]: Jsonisable } }): Promise<IResponse> {
		if (!wsp.isAvailable) await wsp.waitForAvailable(uiContext);
		await wsp.wspServer.wspsLive.saveItems(wsp.code, itemIdents);
		const path = `${encodeURIComponent(wsp.code)}/lifeCycle/ExecTransition?codeTransition=${encodeURIComponent(transition)}`;
		return wsp.wspServer.config.wspSvcUrl.fetch(path, null, {method: "POST", body: IO.fd("refUris", itemIdents.map(SRC.srcRef).join('\t'), "paramsTransition", paramsTransition ? JSON.stringify(paramsTransition) : null)});
	}


	/**
	 *
	 * @param wsp
	 * @param uiContext
	 * @param srcRef
	 * @param codeGenStack
	 * @param addExtraInfos
	 * @param customFullUriDest : spécification libre d'une fullUri de destination (usage : styler)
	 * 	ATTENTION : nécessite des habilitations dédiées
	 */
	export async function fetchGenInfo(wsp: Wsp, uiContext: HTMLElement, srcRef: srcRef, codeGenStack: string, addExtraInfos?: boolean, customFullUriDest?: string): Promise<JGenInfo> {
		if (!wsp.isAvailable) await wsp.waitForAvailable(uiContext);
		let qs = IO.qs("cdaction", "GetGenInfo", "format", "json", "param", wsp.code, "refUri", srcRef, "codeGenStack", codeGenStack, "addExtraInfos", addExtraInfos ? "" : null)
		if (customFullUriDest)
			qs += IO.query(false, "customFullUriDest", customFullUriDest);
		return wsp.wspServer.config.wspGenUrl.fetchJson<JGenInfo>(qs);
	}

	export function getGenSkinIllusUrl(wsp: Wsp, genNode: IItemPubNode, skin: JGenSkin): string {
		const idx = genNode.codeGenStack.lastIndexOf('_');
		const cdGen = idx < 0 ? genNode.codeGenStack : genNode.codeGenStack.substring(idx + 1);
		return WSP.skinSetsIconUrl(wsp, skin.code, genNode.genSkinClass);
	}

	/**
	 * Génération
	 * @param wsp
	 * @param uiContext
	 * @param srcRef
	 * @param codeGenStack
	 * @param props
	 * @param customFullUriDest : spécification libre d'une fullUri de destination (usage : styler)
	 * 	ATTENTION : nécessite des habilitations dédiées
	 */
	export async function generate(wsp: Wsp, uiContext: HTMLElement, srcRef: srcRef, codeGenStack: string, props: Jsonisable, customFullUriDest?: string): Promise<void> {
		if (!wsp.isAvailable) await wsp.waitForAvailable(uiContext);
		await wsp.wspServer.wspsLive.saveAllHouses();
		let qs = IO.qs("cdaction", "Generate", "format", "none", "param", wsp.code, "refUri", srcRef, "codeGenStack", codeGenStack);
		if (customFullUriDest)
			qs += IO.query(false, "customFullUriDest", customFullUriDest);
		if (props) {
			return wsp.wspServer.config.wspGenUrl.fetchVoid(qs, {method: "POST", body: IO.fd("genProps", CDM.stringify(props))});
		} else {
			return wsp.wspServer.config.wspGenUrl.fetchVoid(qs, {method: "PUT"});
		}
	}

	export function getGenPubUrl(wsp: Wsp, uriPub: string): string {
		const root = wsp.wspServer.config.pubUrl.url;
		if (root.endsWith('/') && uriPub.startsWith('/')) uriPub = uriPub.substring(1);
		return root + encodeURI(uriPub) + "?auth";
	}

	/**
	 * @param wsp
	 * @param srcRef
	 * @param codeGenStack
	 * @param customFullUriDest : spécification libre d'une fullUri de destination (usage : styler)
	 * 	ATTENTION : nécessite des habilitations dédiées
	 */
	export function getGenDownloadUrl(wsp: Wsp, srcRef: srcRef, codeGenStack: string, customFullUriDest?: string): string {
		let qs = IO.qs("cdaction", "Download", "param", wsp.code, "refUri", srcRef, "codeGenStack", codeGenStack);
		if (customFullUriDest)
			qs += IO.query(false, "customFullUriDest", customFullUriDest);
		return wsp.wspServer.config.wspGenUrl.url + qs;
	}


	export async function setShortDescTransferToClipboard(ctx: IShortDescCtx, liveProps?: Dict<any>): Promise<void> {
		const data = {
			sdTrsfId: "scenari:shortDescsTransfer:" + Math.floor(Math.random() * 1_000_000_000).toString(36),
			server: ctx.reg.env.universe.config.universeUrl.url,
			wspCd: ctx.reg.env.wsp.code,
			srcRefs: []
		} as JShortDescsTransfer;
		const txt = CLIPBOARD.hasCustomType ? [] : [data.sdTrsfId, data.server, data.wspCd];
		for (const sd of ctx.shortDescs) {
			txt.push(ITEM.srcUriSub(sd));
			data.srcRefs.push(ITEM.srcRefSub(sd));
		}
		let p = CLIPBOARD.writeInClipboard(() => {
			if (CLIPBOARD.hasCustomType) return navigator.clipboard.write([new ClipboardItem({
				"text/plain": new Blob([txt.join('\n')], {type: "text/plain"}),
				[MIMETYPE_SHORTDESC_ID]: new Blob([data.sdTrsfId], {type: MIMETYPE_SHORTDESC_ID}),
				[MIMETYPE_SHORTDESC_JSON]: new Blob([JSON.stringify(data)], {type: MIMETYPE_SHORTDESC_JSON})
			})]);
			return navigator.clipboard.writeText(txt.join('\n'));
		}, ctx.emitter);

		(data as IShortDescsTransfer).wsp = ctx.reg.env.wsp;
		(data as IShortDescsTransfer).shortDescs = ctx.shortDescs;
		if (liveProps) Object.assign(data, liveProps);
		shortDescDatasClipboard = data as IShortDescsTransfer;
		return p;
	}

	/**
	 * A appeler sur un event "dragstart".
	 * @see ITEM.resetShortDescTransferToDragSession
	 */
	export function setShortDescTransferToDragSession(ctx: IShortDescCtx, startDragEv: DragEvent, dragImg: 'native' | 'build') {
		const data = {
			sdTrsfId: "scenari:shortDescsTransfer:" + Math.floor(Math.random() * 1_000_000_000).toString(36),
			server: ctx.reg.env.universe.config.universeUrl.url,
			wspCd: ctx.reg.env.wsp.code,
			srcRefs: ctx.shortDescs.map(ITEM.srcRefSub)
		} as JShortDescsTransfer;
		startDragEv.dataTransfer.effectAllowed = "all";
		startDragEv.dataTransfer.setData(MIMETYPE_SHORTDESC_ID, data.sdTrsfId);
		startDragEv.dataTransfer.setData(MIMETYPE_SHORTDESC_JSON, JSON.stringify(data));
		if (dragImg === 'build') {
			//TODO canvas de l'item ou d'un text "X items"
			const img = new Image();
			img.src = ctx.reg.env.universe.config.skinUrl.url + "wsp/itemType/icons/itemNull/icovalid.png";
			startDragEv.dataTransfer.setDragImage(img, 12, 12);
		}
		(data as IShortDescsTransfer).wsp = ctx.reg.env.wsp;
		(data as IShortDescsTransfer).shortDescs = ctx.shortDescs;
		shortDescDatasDrag = data as IShortDescsTransfer;
	}

	/**
	 * Appel type : this.addEventListener('dragend', ITEM.resetShortDescTransferToDragSession);
	 * @see ITEM.setShortDescTransferToDragSession
	 * */
	export function resetShortDescTransferToDragSession() {
		shortDescDatasDrag = null;
	}

	export function getShortDescsTransferFromDragSession(reg: IReg<IWspUiEnv>, dataTransfer: DataTransfer, transferCtx: OTransferSrcContext, uiCtx?: HTMLElement): IShortDescsTransfer | null {
		if (shortDescDatasDrag) return shortDescDatasDrag;
		return getShortDescsTransferFromExternal(reg, dataTransfer, transferCtx, uiCtx, true);
	}

	export function getShortDescsTransferFromExternal(reg: IReg<IWspUiEnv>, dataTransfer: DataTransfer, transferCtx: OTransferSrcContext, uiCtx?: HTMLElement, fromDrag?: boolean): IShortDescsTransfer | null {
		if (!transferCtx.canCreate) return null;
		if (dataTransfer.types.indexOf('Files') >= 0) {
			//Import de fichiers du FileSystem.
			return reg.getSvc<(reg: IReg<IWspUiEnv>, dataTransfer: DataTransfer, uiCtx?: HTMLElement, fromDrag?: boolean) => IShortDescsTransfer | null>("getShortDescsTransferFromFiles", function (reg: IReg<IWspUiEnv>, dataTransfer: DataTransfer, uiCtx?: HTMLElement, fromDrag?: boolean) {
				let result: ShortDescsFromFile = null;
				const wsp = reg.env.wsp;
				const arr = fromDrag ? dataTransfer.items : dataTransfer.files;
				for (let i = 0; i < arr.length; i++) {
					const type = arr[i].type;
					// File dispo SSi pas modes 'read/write' / 'read-only' de l'event
					// cf https://html.spec.whatwg.org/multipage/dnd.html#dom-datatransferitem-getasfile
					// cf https://html.spec.whatwg.org/multipage/dnd.html#dndevents
					const fileName = (arr[i] instanceof File ? (arr[i] as File) : (arr[i] as DataTransferItem).getAsFile())?.name;
					const itemTypes = type || fileName ? wsp.wspMetaUi.getItemTypes().filter((it: ItemType) => it && it.matchType(type, fileName) > 1) : null;
					if (!result) result = new ShortDescsFromFile(reg, uiCtx, fromDrag);
					if (itemTypes?.length > 0) for (const it of itemTypes) new ShortDescToImportFromFile(result, it, i);
					else new ShortDescToImportFromFile(result, null, i); //pour ajout dans des ressources d'un item ou ajout d'espace
				}
				//console.log("getShortDescTransferFromDragSession:::Files::", result);
				return result;
			})(reg, dataTransfer, uiCtx, fromDrag);
		} else if (dataTransfer.types.indexOf('text/uri-list') >= 0) {
			//Import à partir d'une liste d'Uris.
			return reg.getSvc<(reg: IReg<IWspUiEnv>, dataTransfer: DataTransfer, uiCtx?: HTMLElement, fromDrag?: boolean) => IShortDescsTransfer | null>("getShortDescsTransferFromUriList", function (reg: IReg<IWspUiEnv>, dataTransfer: DataTransfer, uiCtx?: HTMLElement, fromDrag?: boolean) {
				//Création d'un item de type remote à partir de cette url, création d'une Remote internalisée, d'un lien web dans le texte, etc.
				return new ShortDescsFromUrlList(reg, uiCtx, fromDrag);
			})(reg, dataTransfer, uiCtx, fromDrag);
		} else if (!fromDrag && dataTransfer.types.indexOf('text/plain') >= 0) {
			const txt = dataTransfer.getData('text/plain');
			if (/^https?:\/\/[-a-zA-Z0-9@:%._+~#=]{1,256}(\.[-a-zA-Z0-9@:%._+~#=]{1,256})+\/([-a-zA-Z0-9()@:%_+.~'#?&/=]*)$/.test(txt)) {
				//On colle une url.
				const customData = new DataTransfer()
				customData.setData('text/uri-list', txt);
				return reg.getSvc<(reg: IReg<IWspUiEnv>, dataTransfer: DataTransfer, uiCtx?: HTMLElement, fromDrag?: boolean) => IShortDescsTransfer | null>("getShortDescsTransferFromUriList", function (reg: IReg<IWspUiEnv>, dataTransfer: DataTransfer, uiCtx?: HTMLElement, fromDrag?: boolean) {
					//Création d'un item de type remote à partir de cette url, création d'une Remote internalisée, d'un lien web dans le texte, etc.
					return new ShortDescsFromUrlList(reg, uiCtx, false).setCustomDataTransfer(dataTransfer);
				})(reg, customData, uiCtx, false);
			}
		}
		return null;
	}

	export async function getShortDescsTransferFromExternalClipboard(reg: IReg<IWspUiEnv>, clipboardItems: ClipboardItem[], transferCtx: OTransferSrcContext, uiCtx?: HTMLElement): Promise<IShortDescsTransfer | null> {
		if (!transferCtx.canCreate) return null;
		const clipboard = clipboardItems[0];
		if (clipboard.types.indexOf('text/uri-list') >= 0) {
			//Import à partir d'une liste d'Uris.
			const customData = new DataTransfer();
			customData.setData('text/uri-list', await CLIPBOARD.text(clipboard, "text/uri-list"));
			return reg.getSvc<(reg: IReg<IWspUiEnv>, dataTransfer: DataTransfer, uiCtx?: HTMLElement, fromDrag?: boolean) => IShortDescsTransfer | null>("getShortDescsTransferFromUriList", function (reg: IReg<IWspUiEnv>, dataTransfer: DataTransfer, uiCtx?: HTMLElement, fromDrag?: boolean) {
				//Création d'un item de type remote à partir de cette url, création d'une Remote internalisée, d'un lien web dans le texte, etc.
				return new ShortDescsFromUrlList(reg, uiCtx, fromDrag).setCustomDataTransfer(customData);
			})(reg, customData, uiCtx, false);
		} else if (clipboard.types.indexOf('text/plain') >= 0) {
			const txt = await CLIPBOARD.text(clipboard, "text/plain");
			if (/^https?:\/\/[-a-zA-Z0-9@:%._+~#=]{1,256}(\.[-a-zA-Z0-9@:%._+~#=]{1,256})+\/([-a-zA-Z0-9()@:%_+.~'#?&/=]*)$/.test(txt)) {
				//On colle une url.
				const customData = new DataTransfer()
				customData.setData('text/uri-list', txt);
				return reg.getSvc<(reg: IReg<IWspUiEnv>, dataTransfer: DataTransfer, uiCtx?: HTMLElement, fromDrag?: boolean) => IShortDescsTransfer | null>("getShortDescsTransferFromUriList", function (reg: IReg<IWspUiEnv>, dataTransfer: DataTransfer, uiCtx?: HTMLElement, fromDrag?: boolean) {
					//Création d'un item de type remote à partir de cette url, création d'une Remote internalisée, d'un lien web dans le texte, etc.
					return new ShortDescsFromUrlList(reg, uiCtx, fromDrag).setCustomDataTransfer(customData);
				})(reg, customData, uiCtx, false);
			}
		}
		return null;
	}

	/**
	 * Retourne ClipboardItem[] si api Clipboard dispo et aucun ShortDescs trouvés : permet de coller des flux (image...)
	 */
	export async function getShortDescsTransferFromClipboard(wspContext: Wsp, uiCtx?: HTMLElement): Promise<IShortDescsTransfer | null | ClipboardItem[]> {
		if (CLIPBOARD.hasCustomType) {
			const cbItems = await CLIPBOARD.read(uiCtx);
			if (cbItems) return getShortDescsTransferFromClipboardItems(cbItems, wspContext, uiCtx);
		}
		//Fallback !CLIPBOARD.hasCustomType ou pour autoriser construction manuelle d'une liste de shortdesc en text/plain.
		return getShortDescTransferFromText(await CLIPBOARD.readText(uiCtx), wspContext, uiCtx);
	}

	export async function getShortDescsTransferFromClipboardItems(cbItems: ClipboardItem[], wspContext: Wsp, uiCtx?: HTMLElement): Promise<IShortDescsTransfer | ClipboardItem[] | null> {
		if (cbItems.length !== 1) return cbItems;
		const cbItem = cbItems[0];
		const id = cbItem.types.indexOf(MIMETYPE_SHORTDESC_ID) >= 0 ? await CLIPBOARD.text(cbItem, MIMETYPE_SHORTDESC_ID) : null;
		if (id) {
			if (shortDescDatasClipboard?.sdTrsfId === id) return shortDescDatasClipboard;
			const json = await CLIPBOARD.text(cbItem, MIMETYPE_SHORTDESC_JSON);
			if (json) {
				const data = JSON.parse(json) as IShortDescsTransfer;
				if (!isJShortDescsTransfer(data)) return null;
				if (wspContext.reg.env.universe.config.universeUrl.url !== data.server) {
					POPUP.showNotifForbidden("Il est impossible d'exploiter des items d'un autre entrepôt.", uiCtx);
					return null;
				}
				return getShortDescsTransfer(wspContext, data.wspCd, uiCtx, data);
			}
		}
		if (cbItem.types.indexOf("text/plain") >= 0) {
			//Fallback !CLIPBOARD.hasCustomType ou pour autoriser construction manuelle d'une liste de shortdesc en text/plain.
			return (await getShortDescTransferFromText(await CLIPBOARD.text(cbItem, "text/plain"), wspContext, uiCtx)) || cbItems;
		}
		return cbItems;
	}

	export async function getShortDescsTransferFromDataTransfer(data: DataTransfer, wspContext: Wsp, uiCtx?: HTMLElement): Promise<IShortDescsTransfer | null> {
		const id = data.getData(MIMETYPE_SHORTDESC_ID);
		if (id) {
			if (shortDescDatasClipboard?.sdTrsfId === id) return shortDescDatasClipboard;
			const json = data.getData(MIMETYPE_SHORTDESC_JSON);
			if (json) {
				const data = JSON.parse(json) as IShortDescsTransfer;
				if (!isJShortDescsTransfer(data)) return null;
				return getShortDescsTransfer(wspContext, data.wspCd, uiCtx, data);
			}
		}
		if (data.types.indexOf("text/plain") >= 0) {
			//Fallback !CLIPBOARD.hasCustomType ou pour autoriser construction manuelle d'une liste de shortdesc en text/plain.
			return getShortDescTransferFromText(data.getData("text/plain"), wspContext, uiCtx);
		}
		return null;
	}

	/**
	 * Parsing de la version text/plain d'une définition de liens.
	 * Fallback !CLIPBOARD.hasCustomType ou pour autoriser construction manuelle d'une liste de shortdesc en text/plain.
	 */
	export async function getShortDescTransferFromText(txt: string, wspContext: Wsp, uiCtx?: HTMLElement): Promise<IShortDescsTransfer | null> {
		if (!txt || !txt.startsWith("scenari:shortDescsTransfer:")) return null;
		const lines = txt.split(/[\n\r]+/);// PAS '\n': Windows rajoute des \r qui se retrouvent dans les lines.
		if (lines.length >= 3) {
			if (shortDescDatasClipboard && shortDescDatasClipboard.sdTrsfId === lines[0] && wspContext.code === shortDescDatasClipboard.wspCd) return shortDescDatasClipboard;
			const server = lines[1];
			const wspCd = lines[2];
			if (wspContext.reg.env.universe.config.universeUrl.url !== lines[1]) {
				//On retourne l'info du server différent pour notif éventuelle à l'utilisateur.
				return {
					sdTrsfId: lines[0],
					server
				} as IShortDescsTransfer;
			}
			const data = {
				sdTrsfId: lines[0],
				server,
				wspCd,
				srcRefs: lines.splice(3),
				wsp: null,
				shortDescs: null
			} as IShortDescsTransfer;
			return getShortDescsTransfer(wspContext, wspCd, uiCtx, data);
		}
		return null
	}


	async function getShortDescsTransfer(wspContext: Wsp, wspCd: string, uiCtx: HTMLElement, data: IShortDescsTransfer) {
		if (data.server && wspContext.reg.env.universe.config.universeUrl.url !== data.server) return data; //todo svc wspImportInterRepos
		if (wspContext.code !== wspCd) {
			try {
				const wspImportInterWsp = wspContext.reg.getSvc<IImportInterWspSvc>("wspImportInterWsp");
				if (!wspImportInterWsp) return null;
				data.wsp = wspContext.wspServer.wspsLive.findWsp(data.wspCd, true);
				if (!data.wsp.isLoaded) await data.wsp.waitForLoad();
				const createSrc = async function (this: IShortDescTransfer, opts: OImportSrcOptions): Promise<JSrcFields | null> {
					return wspImportInterWsp.importSrc(data.wsp, this, opts, wspContext.reg, uiCtx)
				}
				data.shortDescs = (await WSP.fetchShortDescs(data.wsp, uiCtx, ...data.srcRefs)).map((src: IShortDescTransfer) => {
					src.createSrc = createSrc
					return src;
				});
				data.isImport = true;
			} catch (e) {
				console.log(e);
				return null;
			}
		} else {
			data.wsp = wspContext;
			data.shortDescs = await WSP.fetchShortDescs(data.wsp, uiCtx, ...data.srcRefs);
		}
		shortDescDatasClipboard = data;
		return data;
	}

	/** Cache du set de shortdesc copiés dans le press-papier. */
	let shortDescDatasClipboard: IShortDescsTransfer;

	/** Cache du set de shortdesc en cours de drag&drop. */
	let shortDescDatasDrag: IShortDescsTransfer;

	/** Mime-type du json d'une liste de shortDesc. */
	const MIMETYPE_SHORTDESC_JSON = "web application/json+scsd";

	/**
	 * Mimetype pour stocker l'ID de la liste du shortDesc courant.
	 * Evite un parsing multiple de MIMETYPE_SHORTDESC_JSON dans une même fenêtre.
	 */
	const MIMETYPE_SHORTDESC_ID = "web text/plain+scsdid";

}

REG.reg.registerSvc<ISrcRenderer>(ITEM.KEY_SrcRenderer, 1, new SrcRenderer());
